import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowLeftRight, Play, Pause, Loader2, GitCompareArrows, ScanLine, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { api, formatDate, scoreBg, videoStreamUrl, videoThumbUrl } from '../lib/api';
import { useLanguage } from '../i18n';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const ClipSide = ({ side, videoRef, label }) => {
  if (!side) return null;
  const score = side.report?.overall_score;
  return (
    <div className="space-y-2" data-testid={`compare-side-${label}`}>
      <div className="relative rounded-[20px] overflow-hidden bg-black aspect-[9/14] border border-white/[0.06]">
        <video
          ref={videoRef}
          src={videoStreamUrl(side.analysis_id, side.has_overlay ? 'overlay' : 'original')}
          playsInline
          muted
          loop
          className="w-full h-full object-contain"
          data-testid={`compare-video-${label}`}
        />
      </div>
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-medium text-white/85 truncate mr-2">{side.author?.name}</span>
        {score != null && (
          <span className={`border rounded-[10px] px-2 py-0.5 font-mono text-xs tabular-nums ${scoreBg(score)}`} data-testid={`compare-score-${label}`}>
            {score}
          </span>
        )}
      </div>
    </div>
  );
};

export default function PitchCompare() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, lang } = useLanguage();

  const [postId, setPostId] = useState(searchParams.get('post') || null);
  const [myClipId, setMyClipId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [swapped, setSwapped] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(0.5); // slow-motion by default for comparative analysis

  const [feedPickerOpen, setFeedPickerOpen] = useState(false);
  const [minePickerOpen, setMinePickerOpen] = useState(false);
  const [feedItems, setFeedItems] = useState(null);
  const [mineItems, setMineItems] = useState(null);

  const refA = useRef(null);
  const refB = useRef(null);

  const applyRate = (rate) => {
    [refA, refB].forEach((r) => {
      if (r.current) r.current.playbackRate = rate;
    });
  };

  useEffect(() => {
    applyRate(speed);
  }, [speed, data, swapped]);

  useEffect(() => {
    if (!postId || !myClipId) return;
    (async () => {
      setLoading(true);
      setData(null);
      try {
        const { data: d } = await api.get('/pitch/compare', { params: { post_a: postId, post_b: `mine:${myClipId}` } });
        setData(d);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [postId, myClipId]);

  const openFeedPicker = async () => {
    setFeedPickerOpen(true);
    if (!feedItems) {
      try {
        const { data: d } = await api.get('/pitch/feed', { params: { limit: 30 } });
        setFeedItems(d.items);
      } catch {
        setFeedItems([]);
      }
    }
  };

  const openMinePicker = async () => {
    setMinePickerOpen(true);
    if (!mineItems) {
      try {
        const { data: d } = await api.get('/analyses', { params: { limit: 50 } });
        setMineItems(d.items.filter((i) => i.status === 'done'));
      } catch {
        setMineItems([]);
      }
    }
  };

  const playBoth = () => {
    applyRate(speed);
    [refA, refB].forEach((r) => {
      if (r.current) {
        r.current.currentTime = 0;
        r.current.play().catch(() => {});
      }
    });
    setPlaying(true);
  };

  const pauseBoth = () => {
    [refA, refB].forEach((r) => r.current && r.current.pause());
    setPlaying(false);
  };

  const sideA = data ? (swapped ? data.b : data.a) : null;
  const sideB = data ? (swapped ? data.a : data.b) : null;

  const feedPost = feedItems?.find((p) => p.post_id === postId);
  const mineClip = mineItems?.find((m) => m.analysis_id === myClipId);

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="compare-page">
      <div className="flex items-center justify-between pt-1">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-white/55 hover:text-white transition-colors" data-testid="compare-back-button">
          <ArrowLeft size={15} /> {t('backToDashboard')}
        </button>
      </div>

      <div className="pt-1">
        <h1 className="font-display font-bold text-2xl tracking-tight text-white flex items-center gap-2.5">
          <GitCompareArrows size={22} className="text-white/70" /> {t('compareTitle')}
        </h1>
        <p className="text-sm text-white/55 mt-1">{t('comparePick')}</p>
      </div>

      {/* pickers */}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={openFeedPicker} data-testid="compare-pick-feed" className="ss-card p-4 text-left hover:border-white/25 transition-colors">
          <div className="font-mono text-[10px] uppercase tracking-wider text-white/40 mb-1 flex items-center gap-1.5"><Flame size={11} /> {t('compareWithFeed')}</div>
          <div className="text-sm font-medium text-white truncate">
            {feedPost ? feedPost.author?.name : postId ? postId : t('selectClip')}
          </div>
        </button>
        <button onClick={openMinePicker} data-testid="compare-pick-mine" className="ss-card p-4 text-left hover:border-white/25 transition-colors">
          <div className="font-mono text-[10px] uppercase tracking-wider text-white/40 mb-1 flex items-center gap-1.5"><ScanLine size={11} /> {t('compareWithMine')}</div>
          <div className="text-sm font-medium text-white truncate">
            {mineClip ? `${formatDate(mineClip.created_at, lang)} \u00b7 ${mineClip.overall_score ?? ''}` : myClipId ? myClipId : t('selectClip')}
          </div>
        </button>
      </div>

      {loading && (
        <div className="ss-card p-10 flex justify-center" data-testid="compare-loading">
          <Loader2 size={20} className="animate-spin text-white/50" />
        </div>
      )}

      {data && !loading && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-4 space-y-4" data-testid="compare-arena">
          <div className="grid grid-cols-2 gap-3">
            <ClipSide side={sideA} videoRef={refA} label="a" />
            <ClipSide side={sideB} videoRef={refB} label="b" />
          </div>
          {/* slow-motion control */}
          <div className="flex items-center justify-between" data-testid="compare-speed-controls">
            <span className="font-mono text-[10px] uppercase tracking-wider text-white/40">{t('slowMotion')}</span>
            <div className="flex gap-1.5">
              {[1, 0.5, 0.25].map((s) => (
                <button
                  key={s}
                  onClick={() => setSpeed(s)}
                  data-testid={`compare-speed-${String(s).replace('.', '_')}`}
                  className={`ss-chip px-3 py-1 font-mono text-[11px] ${speed === s ? 'ss-chip-active' : ''}`}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            {playing ? (
              <button onClick={pauseBoth} data-testid="compare-pause-both" className="ss-btn-glass flex-1 h-12 text-sm font-medium flex items-center justify-center gap-2">
                <Pause size={16} /> {t('pauseBoth')}
              </button>
            ) : (
              <button onClick={playBoth} data-testid="compare-play-both" className="ss-btn-primary flex-1 h-12 font-display font-bold text-sm flex items-center justify-center gap-2">
                <Play size={16} /> {t('playBoth')}
              </button>
            )}
            <button onClick={() => setSwapped((s) => !s)} data-testid="compare-swap" className="ss-btn-glass w-12 h-12 flex items-center justify-center" aria-label={t('swap')}>
              <ArrowLeftRight size={17} />
            </button>
          </div>
        </motion.div>
      )}

      {/* Feed picker dialog */}
      <Dialog open={feedPickerOpen} onOpenChange={setFeedPickerOpen}>
        <DialogContent className="bg-[#1E1E1E] border-white/10 rounded-[20px] max-w-sm" data-testid="compare-feed-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{t('compareWithFeed')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {feedItems === null ? (
              <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-white/50" /></div>
            ) : feedItems.length === 0 ? (
              <p className="text-sm text-white/45 text-center py-6">{t('pitchEmpty')}</p>
            ) : (
              feedItems.map((p) => (
                <button
                  key={p.post_id}
                  onClick={() => { setPostId(p.post_id); setFeedPickerOpen(false); }}
                  data-testid={`compare-feed-option-${p.post_id}`}
                  className={`w-full p-2.5 rounded-[14px] border text-left flex items-center gap-3 transition-colors ${
                    postId === p.post_id ? 'border-white/30 bg-white/[0.08]' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
                  }`}
                >
                  <div className="w-12 h-9 rounded-[10px] overflow-hidden bg-black/60 border border-white/10 shrink-0">
                    {p.has_thumbnail && <img src={videoThumbUrl(p.analysis_id)} alt="" className="w-full h-full object-cover" loading="lazy" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-white truncate">{p.author?.name}</div>
                    <div className="font-mono text-[10px] text-white/40 truncate">{lang === 'it' ? p.report?.move_label_it : p.report?.move_label_en}</div>
                  </div>
                  {p.report?.overall_score != null && (
                    <span className={`border rounded-[10px] px-1.5 py-0.5 font-mono text-[11px] tabular-nums ${scoreBg(p.report.overall_score)}`}>{p.report.overall_score}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* My clips picker dialog */}
      <Dialog open={minePickerOpen} onOpenChange={setMinePickerOpen}>
        <DialogContent className="bg-[#1E1E1E] border-white/10 rounded-[20px] max-w-sm" data-testid="compare-mine-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{t('chooseMyClip')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {mineItems === null ? (
              <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-white/50" /></div>
            ) : mineItems.length === 0 ? (
              <p className="text-sm text-white/45 text-center py-6" data-testid="compare-no-mine">{t('noDoneAnalyses')}</p>
            ) : (
              mineItems.map((m) => (
                <button
                  key={m.analysis_id}
                  onClick={() => { setMyClipId(m.analysis_id); setMinePickerOpen(false); }}
                  data-testid={`compare-mine-option-${m.analysis_id}`}
                  className={`w-full p-2.5 rounded-[14px] border text-left flex items-center gap-3 transition-colors ${
                    myClipId === m.analysis_id ? 'border-white/30 bg-white/[0.08]' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
                  }`}
                >
                  <div className="w-12 h-9 rounded-[10px] overflow-hidden bg-black/60 border border-white/10 shrink-0">
                    {m.has_thumbnail && <img src={videoThumbUrl(m.analysis_id)} alt="" className="w-full h-full object-cover" loading="lazy" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-white truncate">{lang === 'it' ? m.move_label_it : m.move_label_en}</div>
                    <div className="font-mono text-[10px] text-white/40">{formatDate(m.created_at, lang)}</div>
                  </div>
                  {m.overall_score != null && (
                    <span className={`border rounded-[10px] px-1.5 py-0.5 font-mono text-[11px] tabular-nums ${scoreBg(m.overall_score)}`}>{m.overall_score}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
